import apiV2 from '../api/v2';
import { routeForAction } from './workflowActionRoutes';
import type { PriorityLevel } from '../components/work-queue/priority/priority';

export interface OutgoingAction {
    action: string;
    label: string;
    actor: 'internal' | 'external' | 'both';
    direction: 'forward' | 'backward' | 'lateral' | 'terminal';
    to: string;
    payloadShape?:
        | 'submission'
        | 'approval'
        | 'rejection'
        | 'records-decision'
        | 'records-with-goal'
        | 'aligner-decision'
        | 'retainer-decision'
        | 'design-plan'
        | 'none';
}

export interface StepTask {
    id: string;
    label: string;
}

// A step's owner, prompt and task for one patient version.
export interface VersionedStatusFields {
    team: string;
    action: string | null;
    task: StepTask | null;
}

export interface StatusMeta {
    id: string;
    code: string; // display shorthand, e.g. '9.1'
    label: string; // clean name - no ordinal prefix
    order: number; // sort position within the macro (gaps of 10, restarts per macro)
    macro: string;
    phase: string;
    team: string;
    action: string | null;
    task: StepTask | null;
    outgoingActions: OutgoingAction[];
    // Retired step: kept so history and parked cases resolve, never offered as a target.
    deprecated: boolean;
    // team/action/task above are the default; a version listed here replaces them.
    byVersion?: { V2?: VersionedStatusFields };
}

// Engine taxonomy as returned by /api/v2/workflows/meta. The Work Queue is
// one of several consumers - keep the type name neutral (WorkflowMeta) even
// though the file lives next to queue-specific code.
export interface WorkflowMeta {
    statuses: StatusMeta[];
    phases: string[];
}

// Legacy alias - keeps existing imports building while components migrate.
export type WorkQueueMeta = WorkflowMeta;

export interface WorkQueueGroup {
    key: string; // InternalStatus value — resolve display via statusCatalog
    phase: string; // display label (e.g. "Refinement")
    team: string; // OwnerTeam value
    action: string | null;
    count: number;
    overdue: number;
    stale: number;
}

export interface WorkQueueVaCounts {
    open: number;
    waitingPatient: number;
    waitingDoctor: number;
}

export interface WorkQueueCase {
    id: string;
    name: string;
    practice: string;
    // Backend-formatted doctor display (e.g. "Dr. Jane Doe"). Null when the
    // practice has no doctor on file or no practice is linked.
    practiceDoctor: string | null;
    /** 'US' | 'CA', or null when the practice has no recognisable country on
     *  file - in which case no badge is shown rather than a guessed one. */
    practiceCountry: string | null;
    statusId: string;
    phase: string;
    team: string;
    action: string | null;
    updatedAt: string; // ISO - when the status was last changed; format on the client
    assignee: string | null;
    profilePictureUrl: string | null; // signed GCS URL when available
    profileThumbnailUrl: string | null; // signed thumbnail URL (320px WebP); null when unavailable
    version: string | null; // 'V1' | 'V2' - drives <VersionBadge />
    priority: PriorityLevel; // 3-level priority — drives the priority pill
    stepEnteredAt: string | null; // ISO - createdAt of latest transition into current status
    phaseEnteredAt: string | null; // ISO - createdAt of latest transition that crossed into current macro
    // Phase milestone dates (date-only at UTC midnight). submittedAt shown on
    // design/redesign steps; approvedAt on manufacturing steps; refiSubmittedAt
    // (the refinementStartDate) on refinement steps from Ref-2.1 onward. Null until reached.
    submittedAt: string | null;
    approvedAt: string | null;
    refiSubmittedAt: string | null;
    // DM aligner progress + monitoring state (from PatientMondayData). Shown in
    // the Treatment-in-progress step column; null elsewhere / when unsynced.
    currentAligner: number | null;
    totalAligners: number | null;
    monitoringStatus: string | null; // 'Started' | 'Paused' | 'Stopped' (capitalized) — raw passthrough
    manufacturer: string | null; // e.g. 'Spark Aligners' — shown in the records-under-review column
    manufacturerCaseStatus: string | null; // manufacturer-reported case status (synced from the portal) — waiting-on-manufacturer steps
    alignerOption: string | null; // 'Active' | 'Starter' (T3P) — drives the T3P tag
    refinementsCount: number | null; // completed refinements; T3P tag only applies while 0
    // Pre-hold status the engine resumes to on exit-hold; null for legacy holds
    // where the engine falls back to the timeline/static edge.
    resumeFromStatus: string | null;
    // Per-team assignee, keyed by team name ('Admin' | 'Clinical' | 'Ortho' | 'DM' | 'TC').
    // A team with no assignment is ABSENT from the map, not null.
    teamAssignees: Record<string, string>;
    // DM-team VAs by open status; present only on the 'Tx - 2. DM VA needed' step.
    vaCounts?: WorkQueueVaCounts;
}

// 'aging'            — approx step-aging (mondayData.updatedAt asc)
// 'phase-aging'      — accurate phase-aging via patient_timeline_event isPhaseChange events
// 'name'             — last name asc, urgency-first partition
// 'aligner-progress' — DM tray advancement ratio (currentAligner/totalAligners) desc
// 'refinements'      — completed refinement rounds (refinementsCount) desc
export type SortBy =
    | 'aging'
    | 'phase-aging'
    | 'name'
    | 'aligner-progress'
    | 'submitted'
    | 'approved'
    | 'refi-submitted'
    | 'refinements';

export type SortDir = 'asc' | 'desc';

export interface FetchCasesParams {
    key: string | null;
    practice?: string[];
    assignee?: string[];
    team?: string[];
    tags?: string[];
    monitoring?: string[];
    manufacturerStatus?: string[];
    priority?: string[];
    sortBy?: SortBy;
    dir?: SortDir;
    limit?: number;
    page?: number;
    splitByPriority?: boolean;
}

export interface PriorityCounts {
    extreme: number;
    high: number;
    normal: number;
}

export interface FetchCasesResponse {
    cases: WorkQueueCase[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    priorityCounts?: PriorityCounts;
}

export interface FetchGroupsResponse {
    groups: WorkQueueGroup[];
}

export async function fetchMeta(): Promise<WorkflowMeta> {
    const response = await apiV2.get('/workflows/meta');
    return response.data.data ?? response.data;
}

export interface FetchGroupsParams {
    practice?: string[];
    assignee?: string[];
    team?: string[];
    tags?: string[];
    monitoring?: string[];
    manufacturerStatus?: string[];
    priority?: string[];
}

export async function fetchGroups(params: FetchGroupsParams = {}): Promise<FetchGroupsResponse> {
    // Filter values are raw names that may contain a comma, so they stay arrays
    // - apiV2 serializes them as repeated params.
    const query: Record<string, string | string[]> = {};
    if (params.practice && params.practice.length > 0) query.practice = params.practice;
    if (params.assignee && params.assignee.length > 0) query.assignee = params.assignee;
    if (params.team && params.team.length > 0) query.team = params.team;
    if (params.monitoring && params.monitoring.length > 0) query.monitoring = params.monitoring;
    if (params.manufacturerStatus && params.manufacturerStatus.length > 0)
        query.manufacturerStatus = params.manufacturerStatus;
    if (params.priority && params.priority.length > 0) query.priority = params.priority;
    const version = (params.tags ?? []).filter((t) => t === 'V1' || t === 'V2');
    if (version.length) query.version = version;
    const response = await apiV2.get('/admin/work-queue/groups', { params: query });
    return response.data.data ?? response.data;
}

// Distinct manufacturer-reported case statuses — options for the
// MANUFACTURER STATUS filter. Values are portal-synced and change over time,
// so the list comes from the data, not a hardcoded enum.
export async function fetchManufacturerStatuses(): Promise<string[]> {
    const response = await apiV2.get('/admin/work-queue/manufacturer-statuses');
    return response.data.data ?? response.data;
}

export async function fetchCases(params: FetchCasesParams): Promise<FetchCasesResponse> {
    const query: Record<string, string | string[]> = {};
    if (params.key) query.key = params.key;
    // Arrays, not comma-joined: same filters as fetchGroups, and the counts and
    // the rows they label must resolve identical values.
    if (params.practice && params.practice.length) query.practice = params.practice;
    if (params.assignee && params.assignee.length) query.assignee = params.assignee;
    if (params.team && params.team.length) query.team = params.team;
    if (params.monitoring && params.monitoring.length) query.monitoring = params.monitoring;
    if (params.manufacturerStatus && params.manufacturerStatus.length)
        query.manufacturerStatus = params.manufacturerStatus;
    if (params.priority && params.priority.length) query.priority = params.priority;
    const version = (params.tags ?? []).filter((t) => t === 'V1' || t === 'V2');
    if (version.length) query.version = version;
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.dir) query.dir = params.dir;
    if (params.limit != null) query.limit = String(params.limit);
    if (params.splitByPriority) query.splitByPriority = 'true';
    query.page = String(params.page ?? 1);

    const response = await apiV2.get('/admin/work-queue/cases', { params: query });
    return response.data.data ?? response.data;
}

// Fetch a single case by ID, bypassing group/filter/pagination scoping. Resolves
// a deep-linked ?case=<id> that isn't in the currently-loaded list. Throws (404)
// when the case doesn't exist or isn't queue-visible.
export async function fetchCase(patientId: string): Promise<WorkQueueCase> {
    const response = await apiV2.get(`/admin/work-queue/cases/${patientId}`);
    return response.data.data ?? response.data;
}

const SEARCH_DEFAULT_LIMIT = 10;

export interface SearchCasesResponse {
    results: WorkQueueCase[];
    total: number;
    page: number;
    limit: number;
    hasMore: boolean;
}

export async function searchCases(
    q: string,
    limit = SEARCH_DEFAULT_LIMIT,
    page = 1
): Promise<SearchCasesResponse> {
    const response = await apiV2.get('/admin/work-queue/search', {
        params: { q, limit, page }
    });
    return response.data.data ?? response.data;
}

// Additive result shape - individual actions may enrich the response
// (e.g. reactivate-case's conversationMirrorFailed); callers that don't
// care about it can keep ignoring the return value.
export interface AdvanceWorkflowResult {
    conversationMirrorFailed?: boolean;
}

export async function advanceWorkflow(
    patientId: string,
    action: string,
    payload?: Record<string, unknown>
): Promise<AdvanceWorkflowResult> {
    const route = routeForAction(action);
    if (!route) {
        throw new Error(`No frontend route registered for workflow action: ${action}`);
    }
    const response = await apiV2.post(`/patients/${patientId}/workflow${route}`, payload ?? {});
    return (response.data?.data as AdvanceWorkflowResult | undefined) ?? {};
}

// Optimistic-concurrency assign/unassign - backend returns 409 if the current
// `assignedTo` doesn't match `expectedAssignedTo` (CAS). Omitting `team`
// targets the case's overall assignee; passing it targets that team's slot.
export async function assignWorkQueueCase(
    patientId: string,
    email: string,
    expectedAssignedTo: string | null,
    team?: string
): Promise<{ assignedTo: string }> {
    const body: { email: string; expectedAssignedTo: string | null; team?: string } = {
        email,
        expectedAssignedTo
    };
    if (team !== undefined) body.team = team;
    const response = await apiV2.post(`/admin/work-queue/cases/${patientId}/assign`, body);
    return response.data.data ?? response.data;
}

// Admin-only override - bypasses the engine edge graph. Reason is required
// and persisted on the timeline event ({ override: true, reason }).
export async function forceWorkflowStatus(
    patientId: string,
    toStatus: string,
    reason: string
): Promise<void> {
    await apiV2.post(`/admin/work-queue/cases/${patientId}/force-status`, { toStatus, reason });
}

export async function unassignWorkQueueCase(
    patientId: string,
    expectedAssignedTo: string | null,
    team?: string
): Promise<{ assignedTo: null }> {
    const body: { expectedAssignedTo: string | null; team?: string } = { expectedAssignedTo };
    if (team !== undefined) body.team = team;
    const response = await apiV2.post(`/admin/work-queue/cases/${patientId}/unassign`, body);
    return response.data.data ?? response.data;
}

export async function setCasePriority(
    patientId: string,
    priority: PriorityLevel,
    reason?: string
): Promise<{ priority: PriorityLevel }> {
    const body: { priority: PriorityLevel; reason?: string } = { priority };
    if (reason !== undefined) body.reason = reason;
    const response = await apiV2.post(`/admin/work-queue/cases/${patientId}/priority`, body);
    return response.data.data ?? response.data;
}
